import { useState, useEffect } from "react";
import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "@/trpc/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SubscriptionGuestBatch } from "@/types/types";

export function useMutateSubscriptionGuests(onPollingEnd?: () => void) {
  const { merchant } = useMerchant();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [uploadReference, setUploadReference] = useState<string | null>(null);
  const [guestBatch, setGuestBatch] = useState<SubscriptionGuestBatch | null>(
    null
  );

  const getUploadUrl = useMutation({
    ...trpc.subscriptionGuests.getUploadUrl.mutationOptions(),
    onSuccess: (data) => {
      setUploadReference(data.reference);
    },
  });

  const batchQuery = useQuery({
    ...trpc.subscriptionGuests.getBatchByReference.queryOptions({
      rentalsMerchantId: merchant?.id || "",
      reference: uploadReference || "",
    }),
    enabled: !!uploadReference && !!merchant?.id,
    refetchInterval: (query) => {
      const data = query.state.data as SubscriptionGuestBatch | undefined;
      if (data?.status === "complete" || data?.status === "error") {
        return false;
      }
      return 2000; // Poll every 2 seconds until the batch is complete or has an error
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!uploadReference) {
      setGuestBatch(null);
      return;
    }
    if (batchQuery.data) {
      setGuestBatch(batchQuery.data);
    }
  }, [batchQuery.data, uploadReference]);

  useEffect(() => {
    if (batchQuery.data?.status === "complete" && merchant?.id) {
      queryClient.invalidateQueries({
        queryKey: trpc.subscriptionGuests.listActive.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.subscriptions.getMetrics.queryKey({
          rentalsMerchantId: merchant.id,
        }),
      });
      onPollingEnd?.();
    }
  }, [batchQuery.data?.status, merchant?.id, onPollingEnd, queryClient, trpc]);

  const resetBatch = () => {
    setGuestBatch(null);
    setUploadReference(null);
  };

  return { getUploadUrl, guestBatch, resetBatch };
}
